import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, FolderOpen, FileText, Plus } from "lucide-react";
import { useClientesCount } from "@/hooks/useClientes";
import { useProjetosCount } from "@/hooks/useProjetos";
import { useOrcamentosCount, useOrcamentosPaginated } from "@/hooks/useOrcamentos";
import { useMemo } from "react";

export function VendedorDashboard() {
  const { data: totalClientes = 0 } = useClientesCount();
  const { data: totalProjetos = 0 } = useProjetosCount();
  const { data: totalOrcamentos = 0 } = useOrcamentosCount();
  const { data: orcamentosPage } = useOrcamentosPaginated(1, 500);

  const orcamentos = orcamentosPage?.data ?? [];
  const stats = useMemo(() => {
    const orcamentosPendentes = Array.isArray(orcamentos)
      ? orcamentos.filter((o) => o.status === "pendente").length
      : 0;
    const orcamentosAprovados = Array.isArray(orcamentos)
      ? orcamentos.filter((o) => o.status === "aprovado").length
      : 0;
    return {
      totalClientes,
      totalProjetos,
      totalOrcamentos,
      orcamentosPendentes,
      orcamentosAprovados,
    };
  }, [totalClientes, totalProjetos, totalOrcamentos, orcamentos]);

  const orcamentosRecentes = useMemo(
    () =>
      (Array.isArray(orcamentos) ? [...orcamentos] : [])
        .sort(
          (a, b) =>
            new Date(b.created_at || "").getTime() -
            new Date(a.created_at || "").getTime(),
        )
        .slice(0, 5),
    [orcamentos],
  );

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      <div className="text-center lg:text-left">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">
          Painel do Vendedor
        </h1>
        <p className="text-lg text-gray-600">
          Visão geral dos seus clientes, projetos e orçamentos
        </p>
      </div>

      <div className="bg-[#C9C9C9] rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Ações Rápidas
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={() => {
              const event = new CustomEvent("mudar_secao", {
                detail: "clientes",
              });
              window.dispatchEvent(event);
            }}
            className="block w-full"
          >
            <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 bg-white hover:bg-gray-50 cursor-pointer">
              <CardContent className="p-4 text-center">
                <div className="flex flex-col items-center space-y-2">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Plus className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Novo Cliente</h3>
                  <p className="text-sm text-gray-600">
                    Cadastrar um novo cliente
                  </p>
                </div>
              </CardContent>
            </Card>
          </button>

          <button
            onClick={() => {
              const event = new CustomEvent("mudar_secao", {
                detail: "projetos",
              });
              window.dispatchEvent(event);
            }}
            className="block w-full"
          >
            <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 bg-white hover:bg-gray-50 cursor-pointer">
              <CardContent className="p-4 text-center">
                <div className="flex flex-col items-center space-y-2">
                  <div className="p-3 rounded-full bg-green-100">
                    <Plus className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Novo Projeto</h3>
                  <p className="text-sm text-gray-600">
                    Criar projeto para um cliente
                  </p>
                </div>
              </CardContent>
            </Card>
          </button>

          <button
            onClick={() => {
              const event = new CustomEvent("mudar_secao", {
                detail: "orcamentos",
              });
              window.dispatchEvent(event);
            }}
            className="block w-full"
          >
            <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 bg-white hover:bg-gray-50 cursor-pointer">
              <CardContent className="p-4 text-center">
                <div className="flex flex-col items-center space-y-2">
                  <div className="p-3 rounded-full bg-purple-100">
                    <Plus className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    Novo Orçamento
                  </h3>
                  <p className="text-sm text-gray-600">
                    Criar orçamento para um projeto
                  </p>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold text-gray-700">
              Clientes
            </CardTitle>
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {stats.totalClientes}
            </div>
            <p className="text-sm text-gray-500">
              Clientes cadastrados por você
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold text-gray-700">
              Projetos
            </CardTitle>
            <div className="p-3 rounded-full bg-green-100">
              <FolderOpen className="h-6 w-6 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {stats.totalProjetos}
            </div>
            <p className="text-sm text-gray-500">
              Projetos vinculados aos seus clientes
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold text-gray-700">
              Orçamentos Pendentes
            </CardTitle>
            <div className="p-3 rounded-full bg-yellow-100">
              <FileText className="h-6 w-6 text-yellow-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {stats.orcamentosPendentes}
            </div>
            <p className="text-sm text-gray-500">
              Aguardando aprovação do cliente
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base font-semibold text-gray-700">
              Orçamentos Aprovados
            </CardTitle>
            <div className="p-3 rounded-full bg-emerald-100">
              <FileText className="h-6 w-6 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {stats.orcamentosAprovados}
            </div>
            <p className="text-sm text-gray-500">
              Orçamentos que viraram pedidos
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            Últimos Orçamentos Criados
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orcamentosRecentes.length > 0 ? (
            <div className="space-y-3">
              {orcamentosRecentes.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      Orçamento #{o.id}
                    </p>
                    <p className="text-sm text-gray-600">
                      {o.cliente?.nome || `Cliente ID: ${o.cliente_id}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {o.created_at
                        ? new Date(o.created_at).toLocaleDateString("pt-BR")
                        : "-"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      R${" "}
                      {Number(o.valor_total || 0).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {o.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Nenhum orçamento encontrado. Crie seu primeiro orçamento para
              começar.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

